import { PlayerSetup } from '~/interfaces';
import { PlayerNumber } from '~/modules/players/player-number';

/** Who sings the round: everyone seated now, not whoever a queued song or the song before recorded.
 * `changed` means the recorded parts no longer describe this line-up. */
export const singersForRound = (
  recorded: PlayerSetup[],
  seated: PlayerNumber[],
): { players: PlayerSetup[]; changed: boolean } => {
  if (!seated.length) return { players: recorded, changed: false };

  const players = seated.map((number) => recorded.find((player) => player.number === number) ?? { number, track: 0 });
  const changed =
    players.length !== recorded.length || players.some((player) => !recorded.some((r) => r.number === player.number));

  return { players, changed };
};

/**
 * The parts a round's singers take, made to fit the song actually starting.
 *
 * Only two singers on a song of several parts choose one - any other count sings the merged track
 * (`GameState.isMergedTrack`). A setup picked on the song card is kept, clamped to parts the song
 * has. One carried over from the last song, which is how a queued song starts, was chosen for a
 * different song: it gets the same default the card would have offered, one part each - rather
 * than both singers on the first part of a duet, or a part a solo song does not have at all, which
 * the engine refuses to start.
 */
export const fitPartsToSong = (
  players: PlayerSetup[],
  tracksCount: number,
  { carriedOver, partsOffered }: { carriedOver: boolean; partsOffered: boolean },
): PlayerSetup[] => {
  const choosesParts = partsOffered && players.length === 2 && tracksCount > 1;
  const lastPart = Math.max(0, tracksCount - 1);

  return players.map((player, index) => ({
    ...player,
    track: !choosesParts ? 0 : carriedOver ? Math.min(index, lastPart) : Math.min(player.track, lastPart),
  }));
};
